// Unpublished Work © 2025 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect, useDispatch} from 'react-redux';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {Stack, AssistanceTooltip, useLinkableText, Box, Typography} from '@deere/isg.component-library';
import {useDeepMemo} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {IconInfoAlert} from '@deere/icons';
import {MOWING_PATTERNS} from 'OnLabor/workboard/constants/mowing-patterns';
import CutsAndDirectionsItem from './cuts-and-directions-item';
import _ from 'lodash';
import moment from 'moment';
import {THIRTY_DAYS} from 'Ui/constants/time-constants';
import {DATE_FORMATS, formatTime} from 'Utils/time-utils';
import {openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {fetchCutHistory, fetchJobTemplates, setSelectedMowingPatterns} from 'Src/ui-core/store/actions/setup-workboard';
import {onLinkClick, getCutHistoryByArea} from 'OnLabor/workboard/utils/setup-workboard';
import {isEmptyArray} from 'Common/utils/validation-utils';

const CUT_HISTORY_LIMIT = 3;

function getJobSettingsLink(openJobsSettingsDialog, getUpdatedJobTemplates, translations, message) {
    const linkProps = {
        actions: [{
            onClick: (e) => {
                onLinkClick(e);
                openJobsSettingsDialog({
                    getUpdatedJobTemplates,
                    translations
                });
            }
        }],
        message
    };

    return useLinkableText(linkProps);
}

function getTooltipBody(openJobsSettingsDialog, getUpdatedJobTemplates, openMowingDirectionsSettingsDialog, translations) {
    const linkableText = getJobSettingsLink(openJobsSettingsDialog, getUpdatedJobTemplates, translations, translations.ONLINK_CUTS_AND_DIRECTIONS_TOOLTIP);

    const footerProps = {
        actions: [{
            onClick: (e) => {
                onLinkClick(e);
                openMowingDirectionsSettingsDialog({
                    translations
                });
            }
        }],
        message: `^${replaceTranslationNames(translations.MANAGE_PLACEHOLDER, {
            '0': translations.ONLINK_MOWING_DIRECTIONS
        })}^`
    };
    const linkableText2 = useLinkableText(footerProps);

    return (
        <Box
            sx={{
                '& a, & .MuiLink-root': {
                    cursor: 'pointer !important'
                }
            }}
        >
            <Stack
                display='block'
                paddingBottom='8px'
            >
                {linkableText}
            </Stack>
            <Stack alignItems='flex-end'>
                {linkableText2}
            </Stack>
        </Box>
    );
}

function WorkboardCutsAndDirections(props) {
    const {
        cutHistory,
        getCutHistory,
        handleFetchJobs,
        handleSelectedMowingDirections,
        loading,
        membership,
        workboard,
        selectedJobs,
        selectedMowingPatterns,
        translations,
        openJobsSettingsDialog,
        openMowingDirectionsSettingsDialog
    } = props;

    const dispatch = useDispatch();

    const mowingPatterns = useDeepMemo(() => {
        if (workboard) {
            return workboard.mowingPatterns;
        }

        return MOWING_PATTERNS.map((mowingPattern) => {
            return {
                area: mowingPattern.area,
                equipmentAreaId: mowingPattern.equipmentAreaId
            };
        });
    }, [workboard]);

    React.useEffect(() => {
        const endTime = formatTime(new Date(), DATE_FORMATS.day);
        const startTime = formatTime(moment().subtract(THIRTY_DAYS, 'days'), DATE_FORMATS.day);

        getCutHistory({
            startTime,
            endTime
        });
    }, []);

    React.useEffect(() => {
        const filteredPatterns = mowingPatterns.filter((m) =>
            _.some(selectedJobs, (job) => job.requiresCutDirections && job.equipmentAreaId === m.equipmentAreaId)
        );

        handleSelectedMowingDirections({
            filteredPatterns,
            selectedMP: selectedMowingPatterns
        });
    }, [selectedJobs, mowingPatterns]);

    const getUpdatedJobTemplates = () => handleFetchJobs({
        dispatcher: dispatch,
        extraContent: null,
        operatorCountLabel: translations.ONLINK_OPERATOR_COUNT
    });
    const tooltipBody = getTooltipBody(openJobsSettingsDialog, getUpdatedJobTemplates, openMowingDirectionsSettingsDialog, translations);
    const emptyStateLink = getJobSettingsLink(openJobsSettingsDialog, getUpdatedJobTemplates, translations, translations.ONLINK_CUTS_AND_DIRECTIONS_EMPTY_BODY);

    return (
        <Stack
            data-id='workboard-cuts-and-directions'
            direction='column'
            padding='16px'
        >
            <Stack
                alignItems='center'
                direction='row'
                marginBottom='16px'
                spacing={1}
            >
                <h3>{translations.ONLINK_CUTS_AND_DIRECTIONS}</h3>
                <AssistanceTooltip
                    body={
                        <Box padding='8px'>
                            <Typography
                                marginBottom='8px'
                                variant='h4'
                            >{replaceTranslationNames(translations.MANAGE_PLACEHOLDER, {
                                    '0': translations.ONLINK_CUTS_AND_DIRECTIONS
                                })}</Typography>
                            {tooltipBody}
                        </Box>
                    }
                >
                    <IconInfoAlert size={16}/>
                </AssistanceTooltip>
            </Stack>
            <Stack
                gap='16px'
                justifyContent={'flex-start'}
            >
                {isEmptyArray(selectedMowingPatterns) && (
                    <Box
                        alignItems={'center'}
                        bgcolor={'#F1F0F0'}
                        border={'4px'}
                        display={'flex'}
                        flexDirection={'column'}
                        gap={'10px'}
                        padding={'16px'}
                        textAlign={'center'}
                    >
                        <Typography variant='labelLarge'>
                            {translations.ONLINK_CUTS_AND_DIRECTIONS_EMPTY_TITLE}
                        </Typography>
                        <Typography variant='bodyMedium'>
                            {emptyStateLink}
                        </Typography>
                    </Box>
                )}
                {!isEmptyArray(selectedMowingPatterns) && selectedMowingPatterns.map((mowingPattern, i) => {
                    return (
                        <div key={i}>
                            <Box
                                sx={{
                                    ...i > 0 && {
                                        'marginTop': '16px'
                                    }
                                }}
                            >
                                <CutsAndDirectionsItem
                                    cutHistory={getCutHistoryByArea(mowingPattern.equipmentAreaId, cutHistory, CUT_HISTORY_LIMIT)}
                                    loading={loading}
                                    membership={membership}
                                    mowingPattern={mowingPattern}
                                    onChange={(name, value) => {
                                        handleSelectedMowingDirections({
                                            index: i,
                                            name,
                                            selectedMP: selectedMowingPatterns,
                                            value
                                        });
                                    }}
                                    translations={translations}
                                />
                            </Box>
                        </div>
                    );
                })}
            </Stack>
        </Stack>
    );
}

WorkboardCutsAndDirections.propTypes = {
    cutHistory: PropTypes.object,
    getCutHistory: PropTypes.func,
    handleFetchJobs: PropTypes.func,
    handleSelectedMowingDirections: PropTypes.func,
    loading: PropTypes.bool,
    membership: PropTypes.membership,
    openJobsSettingsDialog: PropTypes.func,
    openMowingDirectionsSettingsDialog: PropTypes.func,
    selectedJobs: PropTypes.arrayOf(PropTypes.object),
    selectedMowingPatterns: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations,
    workboard: PropTypes.object
};

export function mapStateToProps(state) {
    return {
        cutHistory: state.setupWorkboard.cutsAndDirections.cutHistory,
        loading: state.setupWorkboard.cutsAndDirections.loading,
        membership: state.membership,
        selectedJobs: state.setupWorkboard.jobs.selectedJobs,
        selectedMowingPatterns: state.setupWorkboard.cutsAndDirections.selectedMowingDirections,
        translations: state.translations,
        myJdPermissions: state.account.myJdPermissions,
        orgId: state.membership.orgId
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        getCutHistory(props) {
            dispatch(fetchCutHistory(props));
        },
        handleFetchJobs(props) {
            dispatch(fetchJobTemplates(props));
        },
        handleSelectedMowingDirections(props) {
            dispatch(setSelectedMowingPatterns(props));
        },
        openJobsSettingsDialog(props) {
            dispatch(openDialog(dialogTypes.JOBS_SETTINGS_DIALOG, props));
        },
        openMowingDirectionsSettingsDialog(props) {
            dispatch(openDialog(dialogTypes.MOWING_DIRECTIONS_SETTINGS_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(WorkboardCutsAndDirections));
